import { shallowRef, watch, Ref, onBeforeMount, onBeforeUnmount } from 'vue'
import {
  deleteQueryParam,
  getQueryParam,
  setQueryParam,
} from '@app/utils/url-helpers'

const defaultZip = <T>(value: T): string => {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
      return value.toString()
    case 'boolean':
      return value.toString()
    default:
      return JSON.stringify(value)
  }
}

/**
 * A composable that allows you to read and write query parameters in the URL.
 * Altering the query parameter will automatically update the URL.
 * @param defaultValue - The default value to use when the query parameter is not present
 * @param options.key - The key of the query parameter to read and write
 * @param options.shouldClearOnEmpty - Whether to clear the query parameter when the value is empty
 * @param options.validator - A function to validate the query parameter
 * @param options.unzip - A function to convert the query parameter to the desired type
 * @param options.zip - A function to convert the value to a string
 * @returns
 */
export const useDeepLinking = <T>(
  defaultValue: T,
  {
    key,
    shouldClearOnEmpty,
    validator,
    unzip,
    zip = defaultZip,
  }: {
    key: string
    shouldClearOnEmpty?: boolean
    validator?: (value: string | (string | null)[]) => boolean
    unzip?: (value: string | (string | null)[]) => T
    zip?: (value: T) => string
  }
): [Ref<T>, () => void] => {
  const query = shallowRef<T>(defaultValue)

  function readQuery(): void {
    const queryValue = getQueryParam(key)

    if (!queryValue || (validator && !validator(queryValue))) {
      return
    }

    if (unzip) {
      query.value = unzip(queryValue)
    } else {
      query.value = queryValue as unknown as T
    }
  }

  onBeforeMount(() => {
    readQuery()
  })

  onBeforeUnmount(() => {
    clear()
  })

  function clear(): void {
    deleteQueryParam(key, {
      search: window.location.search,
      replace: true,
    })
  }

  watch(query, (newValue, oldValue) => {
    const isDefault = newValue === defaultValue

    if (newValue === oldValue || isDefault) {
      return
    }

    const effectiveValue = query.value as T

    const isEmptyAndShouldClear = !newValue && shouldClearOnEmpty

    if (isEmptyAndShouldClear) {
      deleteQueryParam(key, {
        search: window.location.search,
        replace: true,
      })
      return
    }

    setQueryParam(key, zip(effectiveValue), {
      search: window.location.search,
      replace: true,
    })
  })

  return [query, clear]
}
